import {
    Component,
    OnInit,
    Input,
    ElementRef,
    signal,
    computed,
    ChangeDetectionStrategy,
    inject,
    linkedSignal,
    effect,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { BudgetContainerComponent } from "./budget/budget.container.component";
import { SeLayoutComponent } from "./layouts/se.layout.component";
import { Se25LiveLinkComponent } from "./layouts/se.25live.link.component";
import { SeOrgDetailsStore } from "./organization/se.org.details.store";
import { EngagementService } from "./engagement-metrics/engagement.service";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { SeSecurityStore } from "./se.security.store";
import { S25OrgDetailsModule } from "../object-details/organization/s25.org.details.module";
import { UpdatedContactRoles } from "../../services/organization.service";
import { contactsFullyRemovedFromRoster } from "../../services/organization/organization.profile.members.util";
import { OrganizationProfileApi } from "../../services/organization/organization.profile.api";
import { S25Array } from "../../util/core/s25.array";
import { S25Promise } from "../../util/core/s25.promise";
import { S25Error } from "../../util/core/s25.error";
import { isUndefined } from "../../util/core";
import { Item } from "../../pojo/Item";
import { SeSearchResultsComponent } from "./search/tile.search.results.component";
import { TileItem } from "./search/item.tile.component";
import { DEFAULT_PAGE_SIZE, SeSearchService, SEEventDocument } from "./search/se.search.service";
import { SeEvent } from "./event/se.event.details.view.component";
import { convertSeEventToTileItem } from "./search/tile.item.converters";
import { S25PreferenceToggleComponent } from "../s25-preference-toggle/s25.preference.toggle.component";
import { OrganizationNewsComponent } from "../../standalone/organization-news/organization.news.component";
import { S25EmailComponent } from "../s25-email/s25.email.component";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { getActiveFormUses } from "../data-ingestion/data.ingestion.const";
import { IngestionFormComponent } from "../data-ingestion/ingestion.form.component";
import { IngestionFormTileComponent } from "../data-ingestion/ingestion.form.tile.component";
import { IngestionCopyableFormsSectionComponent } from "../data-ingestion/ingestion.copyable.forms.section.component";
import { createOrgEditHydrate } from "../data-ingestion/org.form.hydrate.factory";
import { FormsModule } from "@angular/forms";
import { SeOrgFilestoreComponent } from "./organization/se.org.filestore.component";
import { S25SidebarComponent } from "../s25-sidebar/s25.sidebar.component";
import { SidebarStore } from "../s25-sidebar/s25.sidebar.store";
import { SeSecurityComponent } from "./securityConfig/se.security.config.component";
import { UserScopedAccessConfigService, AcctContRightGrant } from "./securityConfig/user.scoped.access.config.service";
import { ModalService } from "../modal/modal.service";
import { ModalDialogParams } from "../modal/modals/modal.dialog.component";
import { SeOrgSecurityStore } from "./se.org.security.store";
import { Proto } from "../../pojo/Proto";
import NumericalString = Proto.NumericalString;
import { LoginStore } from "../../stores/login.store";
import { UserStore } from "../../stores/user.store";
import { StateStore } from "../../services/state/state.store";
import { SeLayoutsConfigComponent } from "./layouts/se.layouts.config.component";
import { DEFAULT_SEARCH_FILTER } from "./search/object-search/se.object.search.filter.component";
import { EventSearchExportOptions } from "./search/object-search/se.event.search.export.service";
import { SeServiceHoursComponent } from "./service-hours/se.service.hours.component";
import { ServiceHoursService } from "./service-hours/service.hours.service";
import { SE_ATTENTION_POLL_MS } from "./student.engagement.util";
import { SeOrgSectionStatusBannerComponent } from "./se.org.section.status.banner.component";
import { SeOrgEventLayoutBannerComponent } from "./se.org.event.layout.banner.component";
import { ContactService } from "../../services/contact.service";
import { Connect25EmailTemplateOrgComponent } from "./email/connect25.email.template.org.component";

type OrgDetailsSections =
    | "details"
    | "budgets"
    | "events"
    | "news"
    | "members"
    | "serviceHours"
    | "security"
    | "files"
    | "website"
    | "contact"
    | "forms"
    | "layouts"
    | "email";

const ORG_DETAILS_SECTIONS: OrgDetailsSections[] = [
    "details",
    "budgets",
    "events",
    "news",
    "members",
    "serviceHours",
    "security",
    "files",
    "website",
    "contact",
    "forms",
    "layouts",
    "email",
];

function isValidOrgTab(tab: string | null): tab is OrgDetailsSections {
    return tab != null && ORG_DETAILS_SECTIONS.includes(tab as OrgDetailsSections);
}

@TypeManagerDecorator("s25-ng-se-org-home")
@Component({
    selector: "s25-ng-se-org-home",
    template: `
        @if (orgDetailsStore.portalAccessDenied()) {
            <p class="noResults">This organization is not available in Connect25.</p>
        } @else if (init()) {
            <div class="header-section">
                <h1>{{ orgDetails()?.name }}</h1>
                <div class="header-actions">
                    @if (canShowJoinUs()) {
                        @if (joinOrgForms().length === 1) {
                            <button
                                class="aw-button aw-button--primary"
                                (click)="openJoinForm(joinOrgForms()[0].ingestionPointId!)"
                            >
                                Join Organization
                            </button>
                        } @else {
                            <select
                                class="cn-form__control form-action-select"
                                aria-label="Join Organization"
                                [ngModel]="selectedJoinFormId()"
                                (ngModelChange)="onJoinFormSelected($event)"
                            >
                                <option [ngValue]="undefined">Join Organization...</option>
                                @for (form of joinOrgForms(); track form.ingestionPointId) {
                                    <option [ngValue]="form.ingestionPointId">
                                        {{ form.ingestionPointName }}
                                    </option>
                                }
                            </select>
                        }
                    }
                    @if (canShowEditModeToggle()) {
                        <div class="edit-toggle-container">
                            <s25-ng-preference-toggle
                                class="c-margin-left--double"
                                prefName="AcctShowInlineEdit"
                                label="Edit Mode"
                                [trueLabel]="'On'"
                                [falseLabel]="'Off'"
                                (modelValueChange)="onPreferenceToggleChange($event)"
                            ></s25-ng-preference-toggle>
                        </div>
                    }
                    <s25-ng-icon-button
                        [type]="'refresh'"
                        [ariaLabel]="'Refresh'"
                        (clicked)="refresh()"
                    ></s25-ng-icon-button>
                </div>
            </div>

            @if (activeJoinFormId()) {
                <div class="section-content join-form-section">
                    <s25-ng-ingestion-form
                        [ingestionPointId]="activeJoinFormId()"
                        [runtimeHydrate]="orgEditRuntimeHydrate()"
                        [showFormExitActions]="true"
                        (onSubmit)="onJoinFormSubmitted()"
                        (onClose)="resetJoinForm()"
                    />
                </div>
            }

            <!-- Navigation buttons for different areas -->
            <div class="nav-buttons">
                @for (navButton of navButtons(); track navButton.itemName) {
                    <button
                        class="aw-button"
                        [class.active]="activeSection() === navButton.section"
                        [title]="navButton.title"
                        [attr.aria-label]="navButtonAriaLabel(navButton)"
                        (click)="activeSection.set(navButton.section)"
                    >
                        {{ navButton.itemName }}
                        @if (navButton.section === "serviceHours" && showServiceHoursAttentionDot()) {
                            <span class="unread-dot unread-dot--label" aria-hidden="true"></span>
                        }
                    </button>
                }
            </div>

            <!-- Content sections -->
            @if (orgDetails()) {
                @switch (activeSection()) {
                    @case ("details") {
                        <div class="section-content">
                            <s25-ng-se-org-section-status-banner section="details" />
                            <s25-ng-se-layout [page]="'organization-details'" />
                            <s25-ng-se-25live-link [itemTypeId]="orgItemTypeId" [itemId]="itemIdSignal()" />
                        </div>
                    }
                    @case ("budgets") {
                        <s25-ng-se-org-section-status-banner section="budgets" />
                        <s25-ng-budget-container [orgId]="itemIdSignal()"></s25-ng-budget-container>
                    }
                    @case ("events") {
                        <div class="section-content">
                            <s25-ng-se-org-section-status-banner section="events" />
                            @if (activeEventFormId()) {
                                <s25-ng-ingestion-form
                                    [ingestionPointId]="activeEventFormId()"
                                    [showFormExitActions]="true"
                                    (onSubmit)="onEventFormSubmitted()"
                                    (onClose)="resetEventForm()"
                                />
                            } @else {
                                <h2>Events</h2>
                                <s25-ng-se-org-event-layout-banner
                                    [orgId]="itemIdSignal()"
                                    [editable]="orgSecStore.canManageLayouts()"
                                />
                                @if (canShowEventCreation()) {
                                    <div class="form-action-row">
                                        @if (eventCreationForms().length === 1) {
                                            <button
                                                class="aw-button aw-button--primary"
                                                (click)="
                                                    activeEventFormId.set(eventCreationForms()[0].ingestionPointId!)
                                                "
                                            >
                                                Create Event
                                            </button>
                                        } @else {
                                            <select
                                                class="cn-form__control form-action-select"
                                                aria-label="Create Event"
                                                [ngModel]="selectedEventFormId()"
                                                (ngModelChange)="onEventFormSelected($event)"
                                            >
                                                <option [ngValue]="undefined">Create Event...</option>
                                                @for (form of eventCreationForms(); track form.ingestionPointId) {
                                                    <option [ngValue]="form.ingestionPointId">
                                                        {{ form.ingestionPointName }}
                                                    </option>
                                                }
                                            </select>
                                        }
                                    </div>
                                }
                                <s25-ng-se-search-results
                                    [results]="upcomingEvents()"
                                    [searchType]="'events'"
                                    [resultsCount]="upcomingEvents().length"
                                    [isSearching]="eventsLoading()"
                                    [eventExport]="orgEventExport()"
                                    [noResultsMessage]="'No upcoming events found. Check back later!'"
                                    (loadMore)="viewMoreEvents()"
                                ></s25-ng-se-search-results>
                            }
                        </div>
                    }
                    @case ("news") {
                        <div class="section-content">
                            <s25-ng-se-org-section-status-banner section="news" />
                            <s25-ng-organization-news [orgId]="itemIdSignal()"></s25-ng-organization-news>
                        </div>
                    }
                    @case ("members") {
                        @if (orgSecStore.canViewMembers()) {
                            <div class="section-content">
                                <s25-ng-se-org-section-status-banner section="members" />
                                <h2>Members</h2>

                                <button class="aw-button aw-button--primary" (click)="openEmailSidebar()">
                                    Email Members
                                </button>

                                <s25-ng-org-contact-roles
                                    [itemId]="itemIdSignal()"
                                    [contacts]="orgDetails().members()"
                                    [canEdit]="isMembersEditable()"
                                    (changes)="onMemberChanged($event)"
                                ></s25-ng-org-contact-roles>
                            </div>
                        }
                    }
                    @case ("forms") {
                        @if (userStore.loggedIn()) {
                            <div class="section-content">
                                @if (activeFormId()) {
                                    <s25-ng-ingestion-form
                                        [ingestionPointId]="activeFormId()"
                                        [runtimeHydrate]="activeOrgFormRuntimeHydrate()"
                                        [showFormExitActions]="true"
                                        (onSubmit)="resetOrgForm()"
                                        (onClose)="resetOrgForm()"
                                    />
                                } @else {
                                    <h2>Forms</h2>
                                    @if (!fillableOrgForms().length) {
                                        <p>No forms available for this organization.</p>
                                    } @else {
                                        <div class="forms-grid">
                                            @for (form of fillableOrgForms(); track form.ingestionPointId) {
                                                <s25-ng-ingestion-form-tile
                                                    [form]="form"
                                                    (formStart)="activeFormId.set($event)"
                                                />
                                            }
                                        </div>
                                    }
                                    @if (orgSecStore.canManageForms()) {
                                        <h3>Copy a Form</h3>
                                        <s25-ng-ingestion-copyable-forms-section [orgId]="itemIdSignal()" />
                                    }
                                }
                            </div>
                        }
                    }
                    @case ("security") {
                        @if (orgSecStore.canViewOrgSecurity()) {
                            <div class="section-content">
                                <s25-ng-se-security
                                    [orgId]="itemIdSignal()"
                                    [orgMembers]="orgDetails()?.members?.() ?? []"
                                    [readOnly]="!orgSecStore.canEditOrgSecurity()"
                                />
                            </div>
                        }
                    }
                    @case ("email") {
                        @if (orgSecStore.canViewOrgEmail()) {
                            <div class="section-content">
                                @if (orgSecStore.canViewMembers()) {
                                    <button
                                        class="aw-button aw-button--primary c-margin-bottom--double"
                                        (click)="openEmailSidebar()"
                                    >
                                        Email Members
                                    </button>
                                }
                                <s25-ng-connect25-email-template-org [orgId]="itemIdSignal()" />
                            </div>
                        }
                    }
                    @case ("files") {
                        @if (orgSecStore.canViewOrgFilestore()) {
                            <div class="section-content">
                                <h2>Files</h2>
                                <se-org-filestore [orgId]="itemIdSignal()"></se-org-filestore>
                            </div>
                        }
                    }
                    @case ("layouts") {
                        @if (orgSecStore.canManageLayouts()) {
                            <div class="section-content">
                                <s25-ng-se-layouts-config
                                    [category]="'specificOrg'"
                                    [orgId]="itemIdSignal()"
                                    [readonly]="!orgSecStore.canManageLayouts()"
                                />
                            </div>
                        }
                    }
                    @case ("website") {
                        <div class="section-content">
                            <h2>Website</h2>
                            <p>Website management functionality will be implemented here.</p>
                        </div>
                    }
                    @case ("serviceHours") {
                        @if (canShowServiceHoursSection()) {
                            @if (orgSecStore.canLogServiceHours()) {
                                <s25-ng-se-org-section-status-banner section="serviceHours" />
                            }
                            <s25-ng-se-service-hours
                                [itemTypeId]="orgItemTypeId"
                                (queueUpdated)="pollOrganizationPendingServiceHoursReview()"
                            ></s25-ng-se-service-hours>
                        }
                    }
                }
            }
        } @else {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }

        <s25-ng-sidebar title="Email Members" [mode]="'overlay'" [width]="600">
            <p class="sidebar-recipients">
                Selected members of: <strong>{{ orgDetails()?.name }}</strong>
            </p>
            @if (sidebarStore.isOpen()) {
                <s25-ng-email
                    [contextId]="itemId"
                    [allowCustomFrom]="true"
                    [itemTypeId]="orgItemTypeId"
                    [orgMembers]="orgDetails()?.members() ?? []"
                    [showFormAssignment]="true"
                    [connect25TemplateOrgIds]="orgSecStore.canViewOrgEmail() ? [itemIdSignal()] : []"
                    (closed)="sidebarStore.close()"
                />
            }
        </s25-ng-sidebar>
    `,
    styles: `
        :host {
            --button--text-align: center;
        }

        .header-section {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
            flex-wrap: wrap;
            gap: 1rem;
        }

        .org-page-denied {
            max-width: 40rem;
        }

        h1 {
            text-align: center;
            margin: 0;
            color: var(--color--text);
            font-size: 2.5rem;
            flex: 1;
        }

        .header-actions {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .edit-toggle-container {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        s25-ng-icon-button {
            flex-shrink: 0;
        }

        .nav-buttons {
            display: flex;
            gap: 1rem;
            margin-bottom: 2rem;
            flex-wrap: wrap;
            justify-content: center;
        }

        .unread-dot {
            width: 8px;
            height: 8px;
            background-color: var(--color--danger);
            border-radius: 50%;
        }

        .unread-dot--label {
            display: inline-block;
            vertical-align: super;
            margin-left: 2px;
        }

        .aw-button {
            padding: 1rem 2rem;
            font-size: 1.1rem;
            font-weight: 600;
            border: 2px solid var(--color--border-bold);
            border-radius: 8px;
            background: var(--color--surface-bg);
            color: var(--color--text-muted);
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 140px;
        }

        .aw-button:hover {
            border-color: var(--cn-blue);
            color: var(--cn-blue);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px color-mix(in srgb, var(--cn-blue) 20%, transparent);
        }

        .aw-button.active {
            background: var(--cn-blue);
            border-color: var(--cn-blue);
            color: var(--color--surface-bg);
            box-shadow: 0 4px 12px color-mix(in srgb, var(--cn-blue) 30%, transparent);
        }

        .section-content {
            background: var(--color--surface-bg);
            border-radius: 8px;
            box-shadow: 0 2px 8px color-mix(in srgb, var(--color--text) 12%, transparent);
            padding: 2rem;
            margin-bottom: 2rem;
        }

        .section-content h2 {
            color: var(--color--text);
            margin-bottom: 1.5rem;
            font-size: 1.8rem;
        }

        .section-content h3 {
            color: var(--color--text-muted);
            margin-bottom: 1rem;
            font-size: 1.3rem;
        }

        .section-content ul {
            list-style: none;
            padding: 0;
        }

        .section-content li {
            padding: 0.75rem;
            border-bottom: 1px solid var(--color--border);
            color: var(--color--text);
        }

        .section-content li:last-child {
            border-bottom: none;
        }

        .section-content .no-events {
            color: var(--color--text-muted);
            font-size: 1rem;
            text-align: center;
            padding: 2rem 0;
        }

        s25-ng-loading-inline-static {
            display: block;
            width: fit-content;
            margin: 0 auto;
        }

        .form-action-row {
            margin-bottom: 1.5rem;
        }

        .form-action-select {
            max-width: 300px;
        }

        .forms-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 1rem;
            margin-bottom: 2rem;
        }

        .sidebar-recipients {
            margin: 0 0 1rem;
            font-size: 13px;
            color: var(--color--text-muted);
            line-height: 1.5;
        }

        @media (max-width: 768px) {
            .header-section {
                flex-direction: column;
                align-items: center;
                margin-bottom: 1.5rem;
            }

            s25-ng-icon-button {
                margin-left: auto;
            }

            h1 {
                font-size: 1.5rem;
                text-align: center;
                width: 100%;
            }

            .header-actions {
                width: 100%;
                justify-content: space-between;
                flex-wrap: wrap;
            }

            :host .header-actions s25-ng-preference-toggle ::ng-deep div {
                margin-left: 0;
            }

            .nav-buttons {
                flex-direction: row;
                flex-wrap: nowrap;
                justify-content: flex-start;
                overflow-x: auto;
                -webkit-overflow-scrolling: touch;
                gap: 0.5rem;
                margin-bottom: 1rem;
                padding: 0.5rem;
                scrollbar-width: thin;
            }

            .aw-button {
                flex: 1 0 auto;
                min-width: auto;
                width: auto;
                padding: 0.6rem 1rem;
                font-size: 0.9rem;
            }

            .section-content {
                padding: 1rem;
                margin-bottom: 1rem;
            }

            .section-content h2 {
                font-size: 1.4rem;
                margin-bottom: 1rem;
            }

            .section-content h3 {
                font-size: 1.1rem;
            }

            .forms-grid {
                grid-template-columns: 1fr;
            }

            .form-action-row {
                margin-bottom: 1rem;
            }

            .form-action-select {
                max-width: 100%;
                width: 100%;
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BudgetContainerComponent,
        SeLayoutComponent,
        Se25LiveLinkComponent,
        S25LoadingInlineModule,
        S25OrgDetailsModule,
        SeSearchResultsComponent,
        S25PreferenceToggleComponent,
        OrganizationNewsComponent,
        S25EmailComponent,
        S25IconButtonComponent,
        IngestionFormComponent,
        IngestionFormTileComponent,
        IngestionCopyableFormsSectionComponent,
        FormsModule,
        SeOrgFilestoreComponent,
        S25SidebarComponent,
        SeSecurityComponent,
        SeLayoutsConfigComponent,
        SeServiceHoursComponent,
        SeOrgSectionStatusBannerComponent,
        SeOrgEventLayoutBannerComponent,
        Connect25EmailTemplateOrgComponent,
    ],
    providers: [SidebarStore],
})
export class SeOrgHomeComponent implements OnInit {
    @Input() itemId: number | NumericalString;
    private readonly securityReady = signal(false);

    readonly orgItemTypeId = Item.Ids.Organization;
    protected itemIdSignal = linkedSignal(() => Number(this.itemId));
    readonly sidebarStore = inject(SidebarStore);
    protected readonly userStore = inject(UserStore);
    securityStore = inject(SeSecurityStore);
    orgSecStore = inject(SeOrgSecurityStore);
    private readonly orgDetailsStore = inject(SeOrgDetailsStore);
    private readonly loginStore = inject(LoginStore);
    private readonly stateStore = inject(StateStore);
    private readonly serviceHoursService = inject(ServiceHoursService);

    private pendingServiceHoursReviewPollSequence = 0;

    readonly hasPendingServiceHoursReview = signal(false);

    readonly canShowServiceHoursSection = computed(
        () => !!this.orgSecStore.canLogServiceHours() || !!this.orgSecStore.canApproveServiceHours(),
    );

    readonly showServiceHoursAttentionDot = computed(
        () => !!this.orgSecStore.canApproveServiceHours() && this.hasPendingServiceHoursReview(),
    );

    canViewOrgForms = signal(true);
    //For forms tab show the org's own forms that have no use, or a use filled out from this tab (org creation, org edit, event creation)
    fillableOrgForms = computed(() =>
        this.orgForms().filter((form) => {
            const activeUses = getActiveFormUses(form.use);
            if (!activeUses.length) {
                return this.userStore.loggedIn();
            }
            return activeUses.some((useKey) => {
                if (useKey === "orgCreation" || useKey === "orgEdit") {
                    return this.userStore.loggedIn(); // the forms themselves can restrict who can fill these out.
                }
                if (useKey === "eventCreation") {
                    return this.orgSecStore.canCreateEvent();
                }
                if (useKey === "orgJoin") {
                    return false;
                }
                return false;
            });
        }),
    );

    /** When the open form is org-edit, wire runtime hydration from the current org page. */
    readonly activeOrgFormRuntimeHydrate = computed(() =>
        this.activeOrgFormUsesOrgEditHydrate() ? this.orgEditRuntimeHydrate() : undefined,
    );

    private readonly activeOrgFormUsesOrgEditHydrate = computed(() => {
        const formId = this.activeFormId();
        if (formId == null) {
            return false;
        }
        return Boolean(this.orgForms().find((form) => form.ingestionPointId === formId)?.use?.orgEdit);
    });

    readonly orgEditRuntimeHydrate = linkedSignal({
        source: this.itemIdSignal,
        computation: (organizationId: number) => createOrgEditHydrate(organizationId),
    });
    activeFormId = signal<number | undefined>(undefined);
    activeEventFormId = signal<number | undefined>(undefined);
    selectedEventFormId = signal<number | undefined>(undefined);
    activeJoinFormId = signal<number | undefined>(undefined);
    selectedJoinFormId = signal<number | undefined>(undefined);

    joinOrgForms = computed(() => this.orgDetailsStore.formsForUse("orgJoin"));

    readonly canShowJoinUs = computed(
        () =>
            this.orgDetailsStore.canUseForm("orgJoin") &&
            this.joinOrgForms().length > 0 &&
            this.activeJoinFormId() == null,
    );

    eventCreationForms = computed(() => this.orgDetailsStore.formsForUse("eventCreation"));
    readonly canShowEventCreation = computed(
        () => this.orgDetailsStore.canUseForm("eventCreation") && this.eventCreationForms().length > 0,
    );

    readonly canShowEditModeToggle = computed(() => {
        if (!this.loginStore.loggedIn()) {
            return false;
        }
        const section = this.activeSection();
        if (section === "details") {
            return !!this.orgSecStore.canEditOrg();
        }
        if (section === "members") {
            return !!this.orgSecStore.canManageMembers();
        }
        return false;
    });
    readonly isEditable = computed(() => !!(this.orgSecStore.canEditOrg() && this.orgDetailsStore.editMode()));
    readonly isMembersEditable = computed(() => this.orgSecStore.canManageMembers() && this.orgDetailsStore.editMode());

    // Computed nav buttons based on permissions
    readonly navButtons = computed<Array<S25ItemI & { section: OrgDetailsSections }>>(() => {
        const buttons: Array<S25ItemI & { section: OrgDetailsSections }> = [];

        // Details - always visible
        buttons.push({
            itemName: "Details",
            destination: "",
            title: "View organization details",
            section: "details",
        });

        // Budgets - requires SE_BUDGET read access
        if (this.orgSecStore.canViewBudget()) {
            buttons.push({
                itemName: "Budgets",
                destination: "",
                title: "Manage organization budgets and financial planning",
                section: "budgets",
            });
        }

        // Events - always visible
        buttons.push({
            itemName: "Events",
            destination: "",
            title: "View and manage upcoming events",
            section: "events",
        });

        buttons.push({
            itemName: "News",
            destination: "",
            title: "View organization news",
            section: "news",
        });

        // Forms - logged-in only; explicit forms rights OR fillable individual forms
        if (this.userStore.loggedIn() && (this.canViewOrgForms() || this.fillableOrgForms().length > 0)) {
            buttons.push({
                itemName: "Forms",
                destination: "",
                title: "Organization Forms",
                section: "forms",
            });
        }

        // Members - requires SE_ORG_CONTACTS read access
        if (this.orgSecStore.canViewMembers()) {
            buttons.push({
                itemName: "Members",
                destination: "",
                title: "Manage organization membership",
                section: "members",
            });
        }

        if (this.canShowServiceHoursSection()) {
            buttons.push({
                itemName: "Service Hours",
                destination: "",
                title: "View and manage organization service hours",
                section: "serviceHours",
            });
        }

        if (this.orgSecStore.canViewOrgSecurity()) {
            buttons.push({
                itemName: "Security",
                destination: "",
                title: "Organization role security and elevated access",
                section: "security",
            });
        }

        // Files - requires SE_ORG_FILESTORE read access
        if (this.orgSecStore.canViewOrgFilestore()) {
            buttons.push({
                itemName: "Files",
                destination: "",
                title: "View and manage organization file attachments",
                section: "files",
            });
        }

        if (this.orgSecStore.canViewOrgEmail()) {
            buttons.push({
                itemName: "Email",
                destination: "",
                title: "Organization email templates",
                section: "email",
            });
        }

        if (this.orgSecStore.canManageLayouts()) {
            buttons.push({
                itemName: "Layouts",
                destination: "",
                title: "Manage organization layouts",
                section: "layouts",
            });
        }

        return buttons;
    });

    activeSection = linkedSignal<OrgDetailsSections>(() => {
        const tab = this.stateStore.seOrgTab();
        return isValidOrgTab(tab) ? tab : "details";
    });

    readonly orgDetails = this.orgDetailsStore.orgDetails;
    readonly init = computed(() => this.securityReady() && !!this.orgDetails());

    // Pagination state for loading more events
    private readonly eventsPage = linkedSignal({ source: this.orgDetailsStore.loadedDetails, computation: () => 1 });
    private readonly eventsPaginateKey = linkedSignal({
        source: this.orgDetailsStore.loadedDetails,
        computation: () => "T",
    });
    eventsLoading = signal(false);
    private readonly eventsHasMore = linkedSignal({
        source: this.orgDetailsStore.loadedDetails,
        computation: (details) => (details?.upcomingEvents?.length ?? 0) >= DEFAULT_PAGE_SIZE,
    });

    readonly orgEventExport = computed(
        (): EventSearchExportOptions => ({
            filter: {
                ...DEFAULT_SEARCH_FILTER,
                organizations: [
                    {
                        itemId: this.itemIdSignal(),
                        itemName: this.orgDetails()?.name ?? "",
                    },
                ],
            },
        }),
    );

    // Transform upcoming events from SeEvent[] to TileItem[] for the search results component
    readonly upcomingEvents = computed<TileItem[]>(() => {
        const details = this.orgDetails();
        if (!details?.upcomingEvents) {
            return [];
        }
        return S25Array.forceArray(details.upcomingEvents).map(convertSeEventToTileItem);
    });

    constructor(private elementRef: ElementRef) {
        this.elementRef.nativeElement.angBridge = this;

        // Set up setter function for AngularJS to call
        // TODO: can't we just set the itemIdSignal and initialize (untracked) in the effect below? (then get rid of ngOnInit)
        this.itemIdSetter = (value: number) => {
            if (this.itemId !== value) {
                this.itemId = value;
                this.itemIdSignal.set(value);
                this.hasPendingServiceHoursReview.set(false);
                if (this.sidebarStore.isOpen()) this.sidebarStore.close();
                this.initializeComponent();
            }
        };

        // Update org security store with current orgId
        effect(() => {
            this.orgSecStore.setOrgId(this.itemIdSignal());
        });

        effect(() => {
            const orgId = this.itemIdSignal();
            if (orgId) EngagementService.recordActivity("ORG_PAGE_VIEW", orgId).catch(() => {});
        });

        effect(() => {
            const details = this.orgDetailsStore.orgDetails();
            if (!details) return;
            this.orgSecStore.setOrgStatusPayload(details.statusEffects ?? [], details.statusMessages ?? []);
        });

        effect(() => {
            if (!this.userStore.loggedIn() && this.activeSection() === "forms") {
                this.activeSection.set("details");
            }
        });

        effect(() => {
            if (!this.canShowServiceHoursSection() && this.activeSection() === "serviceHours") {
                this.activeSection.set("details");
            }
        });

        effect((onCleanup) => {
            if (
                !this.loginStore.loggedIn() ||
                !this.securityReady() ||
                this.orgSecStore.canApproveServiceHours() !== true
            ) {
                this.hasPendingServiceHoursReview.set(false);
                return;
            }
            const orgId = this.itemIdSignal();
            this.hasPendingServiceHoursReview.set(false);
            if (!orgId) {
                return;
            }
            void this.pollOrganizationPendingServiceHoursReview();
            const timer = setInterval(
                () => void this.pollOrganizationPendingServiceHoursReview(),
                SE_ATTENTION_POLL_MS,
            );
            onCleanup(() => clearInterval(timer));
        });

        effect(() => {
            if (!this.orgSecStore.canViewMembers() && this.activeSection() === "members") {
                this.activeSection.set("details");
            }
        });

        effect(() => {
            if (!this.orgSecStore.canViewOrgEmail() && this.activeSection() === "email") {
                this.activeSection.set("details");
            }
        });
    }

    // Bridge function for AngularJS to call
    itemIdSetter: (value: number) => void;

    async ngOnInit() {
        this.initializeComponent();
    }

    private async initializeComponent(): Promise<void> {
        this.resetFormState();
        if (!this.itemId) {
            console.warn("No itemId provided, cannot load organization details.");
            return;
        }
        await this.securityStore.ensureInitialized();
        this.securityReady.set(true);
    }

    private resetFormState(): void {
        this.eventsLoading.set(false);
        this.activeFormId.set(undefined);
        this.activeEventFormId.set(undefined);
        this.selectedEventFormId.set(undefined);
    }

    async viewMoreEvents(): Promise<void> {
        const details = this.orgDetails();
        if (!details || this.eventsLoading() || !this.eventsHasMore()) {
            return;
        }

        try {
            this.eventsLoading.set(true);
            const nextPage = this.eventsPage() + 1;
            const [response, error] = await S25Promise.Maybe(
                SeSearchService.getUpcomingEvents(nextPage, DEFAULT_PAGE_SIZE, this.eventsPaginateKey(), {
                    organization_id: [this.itemIdSignal()],
                }),
            );

            if (error) {
                console.error("Error loading more events:", error);
                return;
            }

            // Transform new events from SEEventDocument[] to SeEvent[]
            const newEvents: SeEvent[] = response.items?.map((event: SEEventDocument) => ({
                itemId: event.itemId,
                itemName: event.itemName,
                date: event.startDate,
                description: event.description,
                locations: event.spaces?.map((space) => space.itemName),
                hostOrg: event.hostOrgId
                    ? {
                          itemId: event.hostOrgId,
                          itemName: event.hostOrgName || "",
                      }
                    : undefined,
            }));

            this.orgDetailsStore.patchDetails({
                upcomingEvents: [...(details.upcomingEvents || []), ...newEvents],
            });
            this.eventsPage.set(nextPage);
            this.eventsPaginateKey.set(response.paginate_key || "T");
            // Check if there are more pages available
            this.eventsHasMore.set(
                response.page_num < response.page_count || (newEvents.length > 0 && response.page_count === 0),
            );
        } catch (error) {
            console.error("Error loading more events:", error);
        } finally {
            this.eventsLoading.set(false);
        }
    }

    resetOrgForm() {
        this.activeFormId.set(undefined);
    }

    onEventFormSelected(formId: number | undefined) {
        this.selectedEventFormId.set(formId);
        if (formId != null) {
            this.activeEventFormId.set(formId);
        }
    }

    resetEventForm() {
        this.activeEventFormId.set(undefined);
        this.selectedEventFormId.set(undefined);
    }

    async onEventFormSubmitted() {
        this.resetEventForm();
        await this.refreshEvents();
    }

    openJoinForm(formId: number) {
        this.activeJoinFormId.set(formId);
    }

    onJoinFormSelected(formId: number | undefined) {
        this.selectedJoinFormId.set(formId);
        if (formId != null) {
            this.activeJoinFormId.set(formId);
        }
    }

    resetJoinForm() {
        this.activeJoinFormId.set(undefined);
        this.selectedJoinFormId.set(undefined);
    }

    async onJoinFormSubmitted() {
        this.resetJoinForm();
        await this.refresh();
    }

    private async refreshEvents() {
        await this.orgDetailsStore.reloadDetails();
    }

    onPreferenceToggleChange(value: boolean | undefined): void {
        if (isUndefined(value)) return;
        this.orgDetailsStore.setEditMode(value);
    }

    async refresh(): Promise<void> {
        this.resetFormState();
        await this.orgDetailsStore.reloadDetails();
        void this.pollOrganizationPendingServiceHoursReview();
    }

    protected navButtonAriaLabel(navButton: S25ItemI & { section: OrgDetailsSections }): string | null {
        if (navButton.section === "serviceHours" && this.showServiceHoursAttentionDot()) {
            return `${navButton.itemName}, needs attention`;
        }
        return null;
    }

    protected pollOrganizationPendingServiceHoursReview(): void {
        if (this.orgSecStore.canApproveServiceHours() !== true) {
            this.hasPendingServiceHoursReview.set(false);
            return;
        }
        const orgId = this.itemIdSignal();
        if (!orgId || !this.loginStore.loggedIn()) {
            this.hasPendingServiceHoursReview.set(false);
            return;
        }
        const requestedOrgId = orgId;
        const pollSequence = ++this.pendingServiceHoursReviewPollSequence;
        this.serviceHoursService
            .hasOrganizationPendingServiceHours(requestedOrgId)
            .then((hasPending) => {
                if (this.itemIdSignal() !== requestedOrgId) {
                    return;
                }
                if (pollSequence !== this.pendingServiceHoursReviewPollSequence) {
                    return;
                }
                this.hasPendingServiceHoursReview.set(hasPending);
            })
            .catch((error: unknown) => {
                if (this.itemIdSignal() !== requestedOrgId) {
                    return;
                }
                if (pollSequence !== this.pendingServiceHoursReviewPollSequence) {
                    return;
                }
                const status = (error as { status?: number })?.status;
                if (status === 401 || status === 403) {
                    this.hasPendingServiceHoursReview.set(false);
                }
            });
    }

    readonly orgForms = computed(() => this.orgDetailsStore.orgScopedForms());

    async onMemberChanged(changes: UpdatedContactRoles): Promise<void> {
        const orgId = this.itemIdSignal();
        const fullyRemoved = contactsFullyRemovedFromRoster(this.orgDetails()?.members?.() ?? [], changes);
        if (fullyRemoved.length && this.orgSecStore.canViewOrgSecurity()) {
            const grants = await UserScopedAccessConfigService.getOrgIndividualGrants(orgId);
            await this.promptAndMaybeRemoveContactGrants(orgId, fullyRemoved, grants);
        }

        const [resp, err] = await S25Promise.Maybe(OrganizationProfileApi.saveMembers(orgId, changes));
        if (err) {
            return S25Error.show(err, "Error updating member roles");
        }

        await this.orgDetailsStore.reloadDetails();
    }

    openEmailSidebar() {
        if (!this.sidebarStore.isOpen()) this.sidebarStore.toggle();
    }

    private async promptAndMaybeRemoveContactGrants(
        orgId: number,
        removedContIds: number[],
        grantRows: AcctContRightGrant[],
    ): Promise<void> {
        if (!removedContIds.length || !grantRows.length) return;

        const grantContIds = new Set(grantRows.map((grant) => grant.contId));
        const affectedContIds = removedContIds.filter((contactId) => grantContIds.has(contactId));
        if (!affectedContIds.length) return;

        const dialogData = ModalService.dialogType("No Yes", {
            title: "Remove individual security grants?",
            message:
                "One or more removed members still have individual security grants on this organization. Remove those grants too?",
        });
        await ModalService.modal("dialog", dialogData);
        if (dialogData.answer !== ModalDialogParams.Answer.Yes) return;

        await Promise.all(
            affectedContIds.map((contactId) =>
                UserScopedAccessConfigService.deleteAllContactOrgGrants(contactId, orgId),
            ),
        );
    }
}
